export type NodeOs = "linux" | "windows" | "macos";
export type NodeArch = "amd64" | "arm64";

export type AutomaticSetupConfig = {
  os: NodeOs;
  arch: NodeArch;
  storagePath: string;
  dataPath: string;
  setupUiPort: string;
  dashboardPort: string;
  nodePort: string;
  autoUpdate: boolean;
  imageTag: string;
};

export const DEFAULT_SETUP_CONFIG: AutomaticSetupConfig = {
  os: "linux",
  arch: "amd64",
  storagePath: "",
  dataPath: "",
  setupUiPort: "8080",
  dashboardPort: "14002",
  nodePort: "28967",
  autoUpdate: true,
  imageTag: "latest",
};

export const OS_OPTIONS: {
  id: NodeOs;
  label: string;
  description: string;
  defaultArch: NodeArch;
  pathHint: string;
}[] = [
  {
    id: "linux",
    label: "Linux",
    description: "Ubuntu, Debian, Fedora, and other Docker hosts",
    defaultArch: "amd64",
    pathHint: "/home/you/silo/storage",
  },
  {
    id: "windows",
    label: "Windows",
    description: "Windows 10/11 with Docker Desktop",
    defaultArch: "amd64",
    pathHint: "C:\\silo\\storage",
  },
  {
    id: "macos",
    label: "macOS",
    description: "Intel or Apple Silicon with Docker Desktop",
    defaultArch: "arm64",
    pathHint: "/Users/you/silo/storage",
  },
];

export const ARCH_OPTIONS: { id: NodeArch; label: string; hint: string }[] = [
  { id: "amd64", label: "amd64", hint: "Most PCs and servers (x86_64)" },
  { id: "arm64", label: "arm64", hint: "Apple Silicon, ARM servers" },
];

export function resolvePorts(config: AutomaticSetupConfig) {
  const webPort = config.setupUiPort.trim() || "8080";
  const consolePort = config.dashboardPort.trim() || "14002";
  const nodeTcpPort = config.nodePort.trim() || "28967";
  const nodeUdpPort = nodeTcpPort;
  return { webPort, consolePort, nodeTcpPort, nodeUdpPort };
}

function resolvePaths(config: AutomaticSetupConfig): { data: string; storage: string } {
  const storageFallback =
    config.os === "windows" ? "C:\\silo\\storage" : "$(pwd)/storage";
  const storage = config.storagePath.trim() || storageFallback;

  if (config.dataPath.trim()) {
    return { data: config.dataPath.trim(), storage };
  }

  if (config.os === "windows") {
    const normalized = storage.replace(/[/\\]+$/, "");
    if (/[/\\]storage$/i.test(normalized)) {
      return {
        data: normalized.replace(/[/\\]storage$/i, "\\data"),
        storage,
      };
    }
    return { data: `${normalized}\\data`, storage };
  }

  const normalized = storage.replace(/\/+$/, "");
  if (normalized.endsWith("/storage")) {
    return {
      data: `${normalized.slice(0, -"/storage".length)}/data`,
      storage,
    };
  }
  if (normalized === "$(pwd)/storage") return { data: "$(pwd)/data", storage };
  const slash = normalized.lastIndexOf("/");
  if (slash > 0) return { data: `${normalized.slice(0, slash)}/data`, storage };
  return { data: `${normalized}/data`, storage };
}

function toDockerPath(path: string): string {
  return path.replace(/\\/g, "/");
}

function containerNameFor(config: AutomaticSetupConfig): string {
  const { webPort, consolePort, nodeTcpPort } = resolvePorts(config);
  const isDefault =
    webPort === "8080" && consolePort === "14002" && nodeTcpPort === "28967";
  return isDefault ? "silo-node" : `silo-node-${nodeTcpPort}`;
}

function buildComposeServiceYaml(
  config: AutomaticSetupConfig,
  dataPathLiteral: string,
): string {
  const tag = config.imageTag.trim() || "latest";
  const name = containerNameFor(config);

  return `services:
  silo-node:
    image: silos3/silo-node-ui:${tag}
    container_name: ${name}
    restart: unless-stopped
    stop_grace_period: 300s
    ports:
      # Host and container ports must match (1:1) and match the env vars below.
      - "\${WEB_PORT:-8080}:\${WEB_PORT:-8080}"
      - "\${SILO_CONSOLE_PORT:-14002}:\${SILO_CONSOLE_PORT:-14002}"
      - "\${SILO_NODE_TCP_PORT:-28967}:\${SILO_NODE_TCP_PORT:-28967}/tcp"
      - "\${SILO_NODE_UDP_PORT:-28967}:\${SILO_NODE_UDP_PORT:-28967}/udp"
    volumes:
      - "${dataPathLiteral}:/app/data"
      - "\${SILO_STORAGE_HOST_PATH:-./storage}:/mnt/silo-storage"
    environment:
      APP_ROOT: "/app"
      HOST: "0.0.0.0"
      WEB_PORT: "\${WEB_PORT:-8080}"
      SILO_NODE_TCP_PORT: "\${SILO_NODE_TCP_PORT:-28967}"
      SILO_NODE_UDP_PORT: "\${SILO_NODE_UDP_PORT:-28967}"
      SILO_CONSOLE_PORT: "\${SILO_CONSOLE_PORT:-14002}"
      SILO_CONSOLE_ADDRESS: "0.0.0.0:\${SILO_CONSOLE_PORT:-14002}"
      SILO_STORAGE_CONTAINER_PATH: "/mnt/silo-storage"
      SILO_STORAGE_HOST_PATH: "\${SILO_STORAGE_HOST_PATH:-./storage}"
      VERSION_SERVER_URL: "https://updater.silos3.com"
      AUTO_UPDATE: "${config.autoUpdate ? "true" : "false"}"
      GOARCH: "\${GOARCH:-${config.arch}}"
`;
}

function buildDotEnvFile(config: AutomaticSetupConfig, storage: string): string {
  const { webPort, consolePort, nodeTcpPort, nodeUdpPort } = resolvePorts(config);
  return [
    `# Values chosen in the portal wizard (edit for another node on this host)`,
    `WEB_PORT=${webPort}`,
    `SILO_CONSOLE_PORT=${consolePort}`,
    `SILO_NODE_TCP_PORT=${nodeTcpPort}`,
    `SILO_NODE_UDP_PORT=${nodeUdpPort}`,
    `SILO_STORAGE_HOST_PATH=${storage}`,
    `GOARCH=${config.arch}`,
    "",
  ].join("\n");
}

function shellQuote(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

/** Inline one-shot CLI (no files). Available on every OS. */
export function buildInlineCliCommand(config: AutomaticSetupConfig): string {
  const { data, storage } = resolvePaths(config);
  const dataDocker = config.os === "windows" ? toDockerPath(data) : data;
  const storageDocker = config.os === "windows" ? toDockerPath(storage) : storage;
  const yaml = buildComposeServiceYaml(config, dataDocker);
  const { webPort, consolePort, nodeTcpPort, nodeUdpPort } = resolvePorts(config);

  if (config.os === "windows") {
    return [
      `# PowerShell - inline (no files)`,
      `$env:WEB_PORT="${webPort}"`,
      `$env:SILO_CONSOLE_PORT="${consolePort}"`,
      `$env:SILO_NODE_TCP_PORT="${nodeTcpPort}"`,
      `$env:SILO_NODE_UDP_PORT="${nodeUdpPort}"`,
      `$env:SILO_STORAGE_HOST_PATH="${storageDocker}"`,
      `$env:GOARCH="${config.arch}"`,
      `New-Item -ItemType Directory -Force -Path ${shellQuote(data)}, ${shellQuote(storage)} | Out-Null`,
      `@'`,
      yaml.trimEnd(),
      `'@ | Set-Content -Encoding utf8 .\\_silo-compose.tmp.yml`,
      `docker compose -f .\\_silo-compose.tmp.yml up -d`,
      `Remove-Item .\\_silo-compose.tmp.yml -ErrorAction SilentlyContinue`,
    ].join("\n");
  }

  return [
    `export WEB_PORT="${webPort}"`,
    `export SILO_CONSOLE_PORT="${consolePort}"`,
    `export SILO_NODE_TCP_PORT="${nodeTcpPort}"`,
    `export SILO_NODE_UDP_PORT="${nodeUdpPort}"`,
    `export SILO_STORAGE_HOST_PATH="${storageDocker}"`,
    `export GOARCH="${config.arch}"`,
    `mkdir -p "${data}" "${storage}" && docker compose -f - up -d <<'EOF'`,
    yaml.trimEnd(),
    `EOF`,
  ].join("\n");
}

/** Short CLI after downloading docker-compose.yml + silo.env */
export function buildFilesCliCommand(config: AutomaticSetupConfig): string {
  const { data, storage } = resolvePaths(config);

  if (config.os === "windows") {
    return [
      `# PowerShell - after downloading docker-compose.yml and silo.env into this folder`,
      `New-Item -ItemType Directory -Force -Path ${shellQuote(data)}, ${shellQuote(storage)} | Out-Null`,
      `docker compose --env-file .\\silo.env up -d`,
    ].join("\n");
  }

  return [
    `# After downloading docker-compose.yml and silo.env into this folder`,
    `mkdir -p "${data}" "${storage}"`,
    `docker compose --env-file ./silo.env up -d`,
  ].join("\n");
}

export function getComposeDownloadFiles(config: AutomaticSetupConfig): {
  composeYml: string;
  envFile: string;
  composeFileName: string;
  envFileName: string;
} {
  const { data, storage } = resolvePaths(config);
  const dataDocker = config.os === "windows" ? toDockerPath(data) : data;
  const storageDocker = config.os === "windows" ? toDockerPath(storage) : storage;

  // For downloaded compose, prefer relative data volume when using default pwd-style paths
  const dataVolume =
    dataDocker === "$(pwd)/data" || dataDocker.endsWith("/data") || dataDocker.endsWith("\\data")
      ? dataDocker.includes("$(pwd)")
        ? "./data"
        : dataDocker
      : dataDocker;

  return {
    composeYml: buildComposeServiceYaml(config, dataVolume),
    envFile: buildDotEnvFile(config, storageDocker),
    composeFileName: "docker-compose.yml",
    // Browsers often block or strip leading-dot downloads; silo.env is reliable.
    envFileName: "silo.env",
  };
}

export type LaunchMode = "inline" | "files";

export function getLaunchPayload(config: AutomaticSetupConfig): {
  inline: { label: string; content: string; hint: string };
  files: {
    label: string;
    content: string;
    hint: string;
    composeYml: string;
    envFile: string;
    composeFileName: string;
    envFileName: string;
  };
} {
  const downloads = getComposeDownloadFiles(config);
  const filesCli = buildFilesCliCommand(config);

  return {
    inline: {
      label: config.os === "windows" ? "PowerShell (inline)" : "Terminal (inline)",
      content: buildInlineCliCommand(config),
      hint:
        "One-shot CLI. Sets env ports, then starts compose without saving permanent files.",
    },
    files: {
      label: "Files + CLI",
      content: filesCli,
      hint:
        "Download docker-compose.yml and silo.env, then run the short CLI in the same folder.",
      composeYml: downloads.composeYml,
      envFile: downloads.envFile,
      composeFileName: downloads.composeFileName,
      envFileName: downloads.envFileName,
    },
  };
}

export const MANUAL_DOCS_URL =
  "https://docs.silos3.com/node/overview/step-by-step-instructions-to-install-silo-node-using-docker";

export const SETUP_UI_URL = "http://127.0.0.1:8080";
export const DASHBOARD_URL = "http://127.0.0.1:14002";
